'use client'

import { useEffect, useRef, useState, Suspense } from 'react'
import dynamic from 'next/dynamic'
import GfSectionMark from '@/components/common/GfSectionMark'
import GfArticleCards from '@/components/common/GfArticleCards'

const ShareButtonsLazy = dynamic(() => import('./ShareButtons'), { ssr: false })
// SEO-важные секции: рендерим в SSR-HTML, иначе Googlebot не видит
// внутренних ссылок (блог→блог, блог→провайдер) и не индексирует посты.
const RecommendedServicesBlockLazy = dynamic(() => import('./RecommendedServicesBlock'), { ssr: true })

function useVisibleOnce(options = { rootMargin: '200px 0px' }) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!ref.current || visible) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true)
        observer.disconnect()
      }
    }, options)
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [visible, options])

  return [ref, visible]
}

export function LazyShareButtons({ title, description, categoryColor, language }) {
  const [ref, visible] = useVisibleOnce()
  return (
    <div ref={ref} className={visible ? 'gf-share' : ''}>
      {visible ? (
        <Suspense fallback={null}>
          <span className="gf-share-label">{language === 'ru' ? 'Поделиться' : 'Share'}</span>
          <ShareButtonsLazy
            gf
            hideLabel
            title={title}
            description={description}
            categoryColor={categoryColor}
            language={language}
          />
        </Suspense>
      ) : null}
    </div>
  )
}

// Рендерим всегда (без IntersectionObserver-гейта) — анкоры на провайдерские
// страницы должны быть в server-HTML для Google.
export function LazyRecommendedServices({ preset, language, categoryColor, pageSlug }) {
  return (
    <Suspense fallback={null}>
      <RecommendedServicesBlockLazy
        preset={preset}
        language={language}
        categoryColor={categoryColor}
        pageSlug={pageSlug}
      />
    </Suspense>
  )
}

// Аналогично — секция «Читайте также» должна быть в server-HTML,
// иначе блог-посты остаются orphan'ами и Google их не индексирует.
export function LazyRelatedArticles({ relatedPosts, language, labels }) {
  return (
    <section className="gf-sec art-related">
      <GfSectionMark eyebrow={labels.subtitle} title={labels.title} />
      <GfArticleCards
        posts={relatedPosts}
        lang={language}
        limit={relatedPosts?.length || 3}
        variant="bento"
      />
    </section>
  )
}
